import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { ChevronLeft, Save, Upload, X, Sparkles } from "lucide-react";

const TONES = [
  { id: "energetic", label: "Energico" },
  { id: "elegant", label: "Elegante" },
  { id: "playful", label: "Giocoso" },
  { id: "professional", label: "Professionale" },
  { id: "bold", label: "Audace" },
];

const BrandForm = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    industry: "personal-brand",
    description: "",
    target_audience: "",
    tone: "energetic",
    logo_base64: "",
    color_primary: "#FF4400",
    color_secondary: "#FFB800",
    hero_image: "",
    hero_label: "",
  });

  // Static fallback list — used if /api/categories fails (e.g. cold start
  // 502, expired token, network blip on mobile). Keeps the brand creation
  // flow usable even when the backend is briefly unreachable.
  const FALLBACK_CATEGORIES = [
    { id: "personal-brand", name: "Personal Brand" },
    { id: "ristorazione", name: "Ristorazione" },
    { id: "fitness", name: "Fitness & Wellness" },
    { id: "ecommerce", name: "E-commerce" },
    { id: "servizi-professionali", name: "Servizi professionali" },
    { id: "immobiliare", name: "Immobiliare" },
    { id: "eventi", name: "Eventi" },
    { id: "beauty", name: "Beauty & Hair" },
    { id: "hotel", name: "Hotel & B&B" },
    { id: "auto", name: "Auto & Moto" },
    { id: "tecnologia", name: "Tecnologia" },
    { id: "salute", name: "Salute & Medicina" },
  ];

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => {
        const list = r?.data?.categories || [];
        setCategories(list.length ? list : FALLBACK_CATEGORIES);
      })
      .catch(() => setCategories(FALLBACK_CATEGORIES));
    if (!isNew) {
      setLoading(true);
      api.get(`/brands/${id}`)
        .then((r) => setForm({
          name: r.data.name,
          industry: r.data.industry,
          description: r.data.description,
          target_audience: r.data.target_audience || "",
          tone: r.data.tone || "energetic",
          logo_base64: r.data.logo_base64 || "",
          color_primary: r.data.color_primary || "#FF4400",
          color_secondary: r.data.color_secondary || "#FFB800",
          hero_image: r.data.hero_image || "",
          hero_label: r.data.hero_label || "",
        }))
        .catch(() => toast.error("Brand non trovato"))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  // Auto-compressor used for both logo and hero. Removes any client-side
  // size cap: the user can upload a 20MB camera photo and we'll downscale
  // + re-encode to fit comfortably under the backend limit.
  //  - `preferPng`: keep PNG (preserves transparency, good for logos).
  //    Falls back to JPEG if the resulting PNG is still too heavy.
  //  - `targetBytes`: target binary size; quality is auto-tuned to fit.
  const compressImage = (file, { maxW, maxH, preferPng = false, targetBytes = 1.2 * 1024 * 1024 }) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          const ratio = Math.min(maxW / width, maxH / height, 1);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          if (preferPng) {
            const pngB64 = (canvas.toDataURL("image/png").split(",")[1]) || "";
            if (pngB64.length * 0.75 <= targetBytes) return resolve(pngB64);
            // Too heavy as PNG (typically photo-style logos): re-encode as
            // JPEG. Transparency is lost but the photo content is preserved.
          }
          const tryAt = (q) => {
            const b64 = (canvas.toDataURL("image/jpeg", q).split(",")[1]) || "";
            if (b64.length * 0.75 <= targetBytes || q <= 0.4) return b64;
            return tryAt(q - 0.1);
          };
          try { resolve(tryAt(0.85)); } catch (e) { reject(e); }
        };
        img.onerror = () => reject(new Error("Immagine non leggibile"));
        img.src = String(reader.result);
      };
      reader.onerror = () => reject(new Error("Errore lettura file"));
      reader.readAsDataURL(file);
    });

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const b64 = await compressImage(f, {
        maxW: 1024, maxH: 1024, preferPng: true, targetBytes: 1.0 * 1024 * 1024,
      });
      setForm((s) => ({ ...s, logo_base64: b64 }));
    } catch (err) {
      toast.error(err?.message || "Errore caricamento logo");
    } finally {
      // Reset so picking the same file again still fires onChange
      e.target.value = "";
    }
  };

  const onHeroChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const b64 = await compressImage(f, {
        maxW: 1080, maxH: 1920, preferPng: false, targetBytes: 1.4 * 1024 * 1024,
      });
      setForm((s) => ({ ...s, hero_image: b64 }));
    } catch (err) {
      toast.error(err?.message || "Errore caricamento foto");
    } finally {
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      toast.error("Nome e descrizione sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      if (isNew) {
        await api.post("/brands", form);
        toast.success("Brand creato!");
      } else {
        await api.put(`/brands/${id}`, form);
        toast.success("Brand aggiornato!");
      }
      navigate("/brands");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link
          to="/brands"
          data-testid="brand-form-back"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="font-mono text-xs uppercase tracking-widest">Brand Profiles</span>
        </Link>

        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
          · {isNew ? "Nuovo" : "Modifica"} Brand
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-10">
          {isNew ? "Nuovo Brand Profile." : "Modifica Brand."}
        </h1>

        <form onSubmit={submit} className="grid md:grid-cols-3 gap-8">
          {/* Logo upload */}
          <div className="md:col-span-1">
            <Label>Logo</Label>
            <div
              className="aspect-square border-2 border-dashed border-zinc-800 bg-zinc-950 rounded-sm flex items-center justify-center relative overflow-hidden group hover:border-[#FF4400] transition-colors"
              style={{ backgroundColor: form.logo_base64 ? "transparent" : undefined }}
            >
              {form.logo_base64 ? (
                <>
                  <img
                    src={`data:image/png;base64,${form.logo_base64}`}
                    alt="Logo"
                    className="w-full h-full object-contain p-4"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, logo_base64: "" }))}
                    className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-black/80 border border-white/10 text-white hover:bg-[#FF4400] flex items-center justify-center"
                    data-testid="brand-logo-remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-2 text-zinc-500 group-hover:text-[#FF4400] transition-colors w-full h-full justify-center">
                  <Upload className="w-6 h-6" />
                  <span className="font-mono text-[10px] uppercase tracking-widest">Upload</span>
                  <span className="text-[10px] text-zinc-700">PNG/JPG · qualsiasi dimensione</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onLogoChange}
                    data-testid="brand-logo-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-3">
              Appare come watermark nel reel
            </p>
          </div>

          <div className="md:col-span-2 space-y-5">
            <Field label="Nome cliente / brand *">
              <input
                type="text"
                data-testid="brand-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Pizzeria Bella Napoli"
                required
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
              />
            </Field>

            <Field label="Settore">
              <select
                data-testid="brand-industry-input"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                disabled={!categories.length}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body disabled:opacity-50 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23a1a1aa%22%20d%3D%22M5%206L0%200h10z%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_1rem_center] pr-10"
              >
                {categories.length === 0 ? (
                  <option value="">Caricamento settori...</option>
                ) : (
                  categories.map((c) => (
                    // The inline style on <option> is what fixes the invisible
                    // dropdown on Android Chrome — without it the OS uses
                    // white-on-white text inside the picker.
                    <option key={c.id} value={c.id} style={{ background: "#18181b", color: "#ffffff" }}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
            </Field>

            <Field label="Descrizione *">
              <textarea
                data-testid="brand-description-input"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Cosa fa, cosa lo rende unico, dove si trova..."
                required
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body resize-none"
              />
            </Field>

            <Field label="Target audience">
              <input
                type="text"
                data-testid="brand-target-input"
                value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                placeholder="Es. Famiglie, giovani 25-40, professionisti"
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
              />
            </Field>

            <Field label="Tono di voce">
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`brand-tone-${t.id}`}
                    onClick={() => setForm({ ...form, tone: t.id })}
                    className={`px-4 py-2 text-sm rounded-sm border transition-all font-body ${
                      form.tone === t.id
                        ? "bg-[#FF4400] border-[#FF4400] text-white font-bold"
                        : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Hero image: real photo used in every reel for this brand */}
            <div className="border border-[#FF4400]/40 bg-[#FF4400]/5 rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div>
                  <Label>Foto personalizzata (hero)</Label>
                  <p className="text-[11px] font-body text-zinc-500 mt-0.5">
                    Una tua foto / ingresso locale / prodotto principale. Apparirà come scena reale in ogni reel.
                  </p>
                </div>
                {form.hero_image && (
                  <button
                    type="button"
                    onClick={() => setForm((s) => ({ ...s, hero_image: "", hero_label: "" }))}
                    className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF4400]"
                    data-testid="brand-hero-remove"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
              <div className="flex gap-3 items-start">
                <label
                  className="cursor-pointer w-24 h-28 border-2 border-dashed border-zinc-800 bg-zinc-950 rounded-sm flex items-center justify-center relative overflow-hidden group hover:border-[#FF4400] transition-colors flex-shrink-0"
                  style={{ aspectRatio: "9/16" }}
                >
                  {form.hero_image ? (
                    <img
                      src={`data:image/jpeg;base64,${form.hero_image}`}
                      alt="Hero"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-zinc-500 group-hover:text-[#FF4400]">
                      <Upload className="w-5 h-5" />
                      <span className="font-mono text-[9px] uppercase tracking-widest">Upload</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onHeroChange}
                    data-testid="brand-hero-input"
                  />
                </label>
                <input
                  type="text"
                  data-testid="brand-hero-label-input"
                  value={form.hero_label}
                  onChange={(e) => setForm({ ...form, hero_label: e.target.value })}
                  placeholder="Etichetta (es. 'io, consulente' o 'entrata ristorante')"
                  className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
                />
              </div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-3">
                Formato verticale consigliato · JPG/PNG · qualsiasi dimensione
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Colore primario">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="brand-color-primary"
                    value={form.color_primary}
                    onChange={(e) => setForm({ ...form, color_primary: e.target.value })}
                    className="w-12 h-11 rounded-sm border border-zinc-800 bg-zinc-900 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.color_primary}
                    onChange={(e) => setForm({ ...form, color_primary: e.target.value })}
                    className="flex-1 px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-white font-mono text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                  />
                </div>
              </Field>

              <Field label="Colore secondario">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    data-testid="brand-color-secondary"
                    value={form.color_secondary}
                    onChange={(e) => setForm({ ...form, color_secondary: e.target.value })}
                    className="w-12 h-11 rounded-sm border border-zinc-800 bg-zinc-900 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.color_secondary}
                    onChange={(e) => setForm({ ...form, color_secondary: e.target.value })}
                    className="flex-1 px-3 py-2.5 bg-zinc-900 border border-zinc-800 text-white font-mono text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                  />
                </div>
              </Field>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Link
                to="/brands"
                className="inline-flex items-center gap-2 px-4 py-3 text-zinc-400 hover:text-white rounded-sm transition-colors"
              >
                Annulla
              </Link>
              <button
                type="submit"
                disabled={loading}
                data-testid="brand-form-submit"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold rounded-sm transition-colors"
              >
                {isNew ? <><Sparkles className="w-4 h-4" />Crea brand</> : <><Save className="w-4 h-4" />Salva</>}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);

export default BrandForm;
